import type { IUserProfileClaim } from '@gik/calendar/models/Claim';
import { getEndDateTime, getStartDateTime } from '@gik/calendar/models/Claim';
import bemBlock from '@gik/core/utils/bemBlock';
import withComponentErrorBoundary from '@gik/core/utils/withComponentErrorBoundary';
import { HoverAnimation } from '@gik/ui-legacy/HoverAnimation';
import { SvgIcon } from '@gik/ui-legacy/SvgIcon/SvgIcon';
import { openClaimedItemModal } from '@gik/user-profile/components/ClaimedItemModal/ClaimedItemModal';
import InformationCircleIcon from '@heroicons/react/outline/InformationCircleIcon';
import React from 'react';

export interface IClaimedItemCardProps {
  claimedItem: IUserProfileClaim;
}

const blockName = 'claimed-item-card';

export function ClaimedItemCardComp({ claimedItem }: IClaimedItemCardProps) {
  const bem = bemBlock(blockName);

  const formattedMonthLength = React.useMemo(() => getStartDateTime(claimedItem).format('MMMM').length, [claimedItem]);
  const dateFormat = React.useMemo(
    () => `ddd. MMM${formattedMonthLength > 3 ? '.' : ''} D, YYYY`,
    [formattedMonthLength]
  );
  const timeFormat = 'h:mma';

  const getFormattedTime = (): JSX.Element => {
    if (claimedItem.entry.allDay) {
      return <>Anytime</>;
    } else {
      return (
        <>
          {getStartDateTime(claimedItem).format(timeFormat)}-{getEndDateTime(claimedItem).format(timeFormat)}
        </>
      );
    }
  };

  return (
    <HoverAnimation className={bem('hover-wrapper')}>
      <div
        onClick={e => {
          e.preventDefault();
          openClaimedItemModal({ claimedItem, getFormattedTime, dateFormat });
        }}
        className={bem()}
        id={claimedItem.id.toString()}
      >
        <section className={bem('first-section')}>
          <section className={bem('dates')}>
            <div className={bem('date')}>{getStartDateTime(claimedItem).format(dateFormat)}</div>
            <span className={bem('flex-separator')} />
            <div className={bem('time')}>{getFormattedTime()}</div>
          </section>

          <section className={bem('title-description-container')}>
            <div className={bem('title')}>{claimedItem.entry.typeName}</div>

            <div className={bem('description')}>{claimedItem.entry.title}</div>
          </section>
        </section>

        <section className={bem('info')}>
          <SvgIcon Icon={InformationCircleIcon} />
        </section>
      </div>
    </HoverAnimation>
  );
}

export const ClaimedItemCard = withComponentErrorBoundary(ClaimedItemCardComp);
